var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { MAX_DEGREE } from './coefficients';
function _getDimension(
// biome-ignore lint/suspicious/noExplicitAny: p5 typing
context, isP3D) {
    var _a;
    var name = (_a = context.constructor) === null || _a === void 0 ? void 0 : _a.name;
    return isP3D ||
        name === 'WebGLRenderingContext' ||
        name === 'WebGL2RenderingContext'
        ? 3
        : 2;
}
// biome-ignore lint/suspicious/noExplicitAny: p5 typing
export function _getCanvasUtils(canvas) {
    var _a;
    // createGraphics wraps a renderer; createCanvas returns the renderer itself.
    var renderer = (_a = canvas === null || canvas === void 0 ? void 0 : canvas._renderer) !== null && _a !== void 0 ? _a : canvas;
    var ctx = renderer === null || renderer === void 0 ? void 0 : renderer.drawingContext;
    if (!ctx)
        throw new Error('[p5.bezier] Canvas is not supported');
    var dimension = _getDimension(ctx, renderer.isP3D);
    var sketch = renderer._pInst;
    if (!sketch) {
        if (dimension === 3) {
            throw new Error('[p5.bezier] WebGL requires a p5.js renderer');
        }
        return {
            dimension: dimension,
            beginPath: ctx.beginPath.bind(ctx),
            moveTo: function (x, y) { return ctx.moveTo(x, y); },
            lineTo: function (x, y) { return ctx.lineTo(x, y); },
            closePath: function (closeType) {
                if (closeType === 'CLOSE')
                    ctx.closePath();
                if (renderer._doFill)
                    ctx.fill();
                if (renderer._doStroke)
                    ctx.stroke();
            },
            beginDash: function () {
                ctx.save();
                ctx.fillStyle = 'rgba(0, 0, 0, 0)';
            },
            endDash: function () { return ctx.restore(); },
        };
    }
    var hasVertices = false;
    var pendingVertex = null;
    return {
        dimension: dimension,
        beginPath: function () {
            sketch.beginShape();
            hasVertices = false;
            pendingVertex = null;
        },
        lineTo: function (x, y, z) {
            if (z === void 0) { z = 0; }
            if (pendingVertex) {
                sketch.vertex.apply(sketch, pendingVertex);
                pendingVertex = null;
            }
            sketch.vertex(x, y, z);
            hasVertices = true;
        },
        moveTo: function (x, y, z) {
            if (z === void 0) { z = 0; }
            // Defer the next vertex so dash gaps don't submit singleton shapes.
            if (hasVertices) {
                sketch.endShape();
                sketch.beginShape();
                hasVertices = false;
            }
            pendingVertex = [x, y, z];
        },
        closePath: function (closeType) {
            sketch.endShape(closeType === 'CLOSE' ? sketch.CLOSE : undefined);
            hasVertices = false;
            pendingVertex = null;
        },
        beginDash: function () {
            sketch.push();
            sketch.noFill();
        },
        endDash: function () { return sketch.pop(); },
    };
}
var warnedSmoothness = false;
export function _validateSmoothness(smoothness) {
    if (smoothness < 1 || smoothness > 5 || !Number.isInteger(smoothness)) {
        if (!warnedSmoothness) {
            warnedSmoothness = true;
            console.warn('[p5.bezier] Smoothness should be an integer between 1 and 5');
        }
        return (Number.isNaN(smoothness)
            ? 3
            : Math.round(Math.max(1, Math.min(5, smoothness))));
    }
    return smoothness;
}
/* -------------------------------------------------------------------------- */
var SeedRandom = /** @class */ (function () {
    function SeedRandom(seed) {
        this.m = Math.pow(2, 31) - 1;
        this.a = 1103515245;
        this.c = 12345;
        this.state = seed;
    }
    SeedRandom.prototype.r = function () {
        this.state = (this.a * this.state + this.c) % this.m;
        return this.state / this.m;
    };
    SeedRandom.prototype.next = function (min, max) {
        return Math.floor(this.r() * (max - min + 1)) + min;
    };
    return SeedRandom;
}());
var seed = 1234;
export function _concentrate(pointList, close) {
    if (close === void 0) { close = false; }
    var save = close ? 3 : 0;
    var limit = MAX_DEGREE - save;
    if (pointList.length <= limit)
        return _copy(pointList);
    var excessPoints = pointList.length - limit;
    var rng = new SeedRandom(seed);
    var rmIndex = new Set();
    __spreadArray([], new Array(excessPoints), true).map(function (_, ind) {
        var r = rng.next(5 + ind, limit - 5 + ind);
        while (rmIndex.has(r)) {
            r = rng.next(5 + ind, limit - 5 + ind);
        }
        rmIndex.add(r);
    });
    return _copy(pointList.filter(function (_, index) { return !rmIndex.has(index); }));
}
export function _copy(arr, target) {
    if (target === void 0) { target = []; }
    target.length = arr.length;
    for (var i = 0; i < arr.length; i++) {
        var source = arr[i];
        var point = target[i];
        if (point) {
            point.length = source.length;
            for (var d = 0; d < source.length; d++)
                point[d] = source[d];
        }
        else {
            target[i] = source.slice();
        }
    }
    return target;
}
export function _samePoints(a, b) {
    if (a.length !== b.length)
        return false;
    for (var i = 0; i < a.length; i++) {
        if (a[i].length !== b[i].length)
            return false;
        for (var d = 0; d < a[i].length; d++) {
            if (a[i][d] !== b[i][d])
                return false;
        }
    }
    return true;
}
export function _getCloseCurvePoints(pointList) {
    var len = pointList.length;
    if (len === 0)
        return [];
    var first = pointList[0];
    var last = pointList[len - 1];
    if (len === 1)
        return _copy([first]);
    var second = pointList[1];
    var secondLast = pointList[len - 2];
    return [
        _interpolateVertex(secondLast, last, 2),
        _interpolateVertex(second, first, 2),
        first.slice(),
    ];
}
export function _interpolateVertex(v1, v2, t) {
    var len1 = v1.length;
    var len2 = v2.length;
    if (len1 === 2 && len2 === 2) {
        return [v1[0] + (v2[0] - v1[0]) * t, v1[1] + (v2[1] - v1[1]) * t];
    }
    if (len1 === 3 && len2 === 3) {
        return [
            v1[0] + (v2[0] - v1[0]) * t,
            v1[1] + (v2[1] - v1[1]) * t,
            v1[2] + (v2[2] - v1[2]) * t,
        ];
    }
    return v2;
}
